import styled from '@emotion/styled';
import { Controller } from 'react-hook-form';
import { FormSelect } from 'ui/components/elements/FormSelect/FormSelect';
import { FormInput } from 'ui/components/elements/FormInput/FormInput';
import { FormError } from 'ui/components/elements/FormError/FormError';
import { FormLabel } from 'ui/components/elements/FormLabel/FormLabel';
import { FormTextArea } from 'ui/components/elements/FormTextArea/FormTextArea';
import { FormCheckbox } from 'ui/components/elements/FormCheckbox/FormCheckbox';
import { ActionButton } from 'ui/components/elements/ActionButton/ActionButton';
import { useModal } from 'ui/components/screens/modals/useModal';
import { ImageUpload } from 'ui/components/features/ImageUpload/ImageUpload';
import { CompanyNameSearch } from 'ui/components/screens/modals/CompanyNameSearch/CompanyNameSearch';
import { TemplateModal } from 'ui/components/screens/modals/TemplateModal/TemplateModal';
import { CompanyInfoVisibilityNotice } from 'ui/components/features/CompanyInfoVisibilityNotice/CompanyInfoVisibilityNotice';
import IconSearch from 'ui/images/form/search.svg';
import MailIcon from 'ui/images/icons/mail.svg';
import { mqUser } from 'utils/responsive';
import { isNotRegistrationComplete } from 'domain/entities/CompanyUser/CompanyUser';
import { Preview } from 'ui/components/company/patterns/CompanyEdit/Preview';
import { getLabel } from 'utils/form';

const Container = styled.div`
    display: flex;
    gap: 5px;
    width: 100%;
    margin: 0 auto;

    ${mqUser('SP')} {
        flex-direction: column;
    }
`;

const Wrapper = styled.div`
    display: flex;
    flex-direction: column;
    gap: 20px;
    width: 100%;
    max-width: 600px;
    margin: 0 auto 20px auto;
    background-color: #ffffff;
    border-radius: 8px;
    padding: 30px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
`;

const ButtonContainer = styled.div`
    margin: 20px auto 0;
`;

const SearchButton = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: var(--dark-blue);
    color: var(--white);
    font-size: var(--font-medium);
    font-weight: 700;
    border-radius: 30px;
    cursor: pointer;
    padding: 15px 25px;
    width: fit-content;

    img {
        margin-left: 8px;
        width: 20px;
        height: 20px;
    }
`;

const Description = styled.div`
    font-size: 13px;
    font-weight: var(--font-bold);
    color: #626262;
    text-align: left;
`;

const FlexContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 5px;
`;

const CheckboxContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
    margin-top: 10px;
    margin-bottom: 20px;
`;

const TemplateWrapper = styled.button`
    display: flex;
    align-items: center;
    justify-content: center;
    background: var(--lightblue);
    border: 2px solid var(--main-color-blue);
    border-radius: 4px;
    cursor: pointer;
    font-size: 14px;
    font-weight: 900;
    gap: 3px;
    padding: 4px 8px;
    color: var(--main-color-blue);
    transition: opacity 0.2s ease-out;
    width: fit-content;
    &:hover {
        opacity: 0.8;
    }
    img {
        width: 15px;
        pointer-events: none;
    }
`;

interface CompanyEditFormProps {
    user: any;
    control: any;
    loading: boolean;
    errors: any;
    areaChoices: any[];
    employeeChoices: any[];
    revenueChoices: any[];
    industryChoices: any[];
    netAssetsLevelChoices: string[][];
    filteredPrefectureChoices: string[][];
    profitLevelChoices: string[][];
    showSnackbar: boolean;
    editing?: boolean;
    setShowSnackbar: (value: boolean) => void;
    submit: () => void;
    setValue: (name: any, value: any) => void;
    watch: (name?: string) => any;
    onCompanySelect: (company: any) => void;
    setEditing?: (editing: boolean) => void;
    unPublish?: () => void;
    reOpen?: () => void;
}

export const CompanyEditForm: React.FC<CompanyEditFormProps> = ({
    user,
    control,
    loading,
    errors,
    areaChoices,
    employeeChoices,
    revenueChoices,
    industryChoices,
    netAssetsLevelChoices,
    filteredPrefectureChoices,
    profitLevelChoices,
    editing = true,
    submit,
    setValue,
    watch,
    onCompanySelect,
    setEditing,
    unPublish,
    reOpen,
}) => {
    const companySearchModal = useModal();
    const templateModal = useModal();

    const handleTemplateClick = () => {
        templateModal.openModal();
    };

    const handleCompanySearchClick = () => {
        companySearchModal.openModal();
    };

    const handleSelectTemplate = (text: string) => {
        const currentQuestion = watch('question') || '';
        if (currentQuestion.trim()) {
            setValue('question', currentQuestion + '\n' + text);
        } else {
            setValue('question', text);
        }
    };

    if (!user) {
        return null;
    }

    const notRegistrationComplete = isNotRegistrationComplete(user);

    return (
        <>
            {companySearchModal.createModal(
                <CompanyNameSearch
                    onSelect={(company) => onCompanySelect(company)}
                    closeModal={companySearchModal.closeModal}
                />,
                { scrollable: false },
            )}
            {templateModal.createModal(
                <TemplateModal
                    onSelect={handleSelectTemplate}
                    onClose={templateModal.closeModal}
                    companyType="company"
                />,
                {
                    keyboard: true,
                    maskClosable: true,
                    onClose: templateModal.closeModal,
                },
            )}
            <Container>
                {editing && (
                    <Wrapper>
                        <Description>
                            {watch('is_sole_proprietor')
                                ? ' 屋号・ホームページURL・ロゴ画像はマッチングしたお相手にのみ開示されます'
                                : '企業名・法人番号・企業画像・ホームページURLはマッチングしたお相手にのみ開示されます'}
                        </Description>
                        <FlexContainer>
                            <FormCheckbox
                                id="is_sole_proprietor"
                                checked={!!watch('is_sole_proprietor')}
                                onChange={() => setValue('is_sole_proprietor', !watch('is_sole_proprietor'))}
                                disabled={!notRegistrationComplete}
                            >
                                <Description>
                                    {notRegistrationComplete
                                        ? '個人事業主の方はこちらをチェックしてください'
                                        : '個人事業主である'}
                                </Description>
                            </FormCheckbox>
                        </FlexContainer>
                        {!watch('is_sole_proprietor') && (
                            <SearchButton onClick={handleCompanySearchClick}>
                                法人を検索する
                                <img src={IconSearch} alt="search" />
                            </SearchButton>
                        )}
                        <FlexContainer>
                            <FormLabel
                                title={watch('is_sole_proprietor') ? '個人事業主名（屋号）' : '企業名'}
                                isRequired
                            />
                            {!watch('is_sole_proprietor') && (
                                <Description>※法人を検索すると自動で入力されます</Description>
                            )}
                            <Controller
                                name="name"
                                control={control}
                                render={({ field }) => (
                                    <div>
                                        <FormInput
                                            {...field}
                                            hasError={!!errors.name?.message}
                                            disabled={!watch('is_sole_proprietor')}
                                        />
                                        {errors.name?.message && <FormError message={errors.name.message} />}
                                    </div>
                                )}
                            />
                        </FlexContainer>
                        {!watch('is_sole_proprietor') && (
                            <FlexContainer>
                                <FormLabel title="法人番号" isRequired />
                                <Description>※法人を検索すると自動で入力されます</Description>
                                <Controller
                                    name="company_code"
                                    control={control}
                                    render={({ field }) => (
                                        <div>
                                            <FormInput
                                                {...field}
                                                value={field.value ?? ''}
                                                hasError={!!errors.company_code?.message}
                                                disabled
                                            />
                                            {errors.company_code?.message && (
                                                <FormError message={errors.company_code.message} />
                                            )}
                                        </div>
                                    )}
                                />
                            </FlexContainer>
                        )}
                        <FlexContainer>
                            <FormLabel title="エリア" isRequired />
                            {areaChoices && areaChoices.length > 0 && (
                                <Controller
                                    name="area"
                                    control={control}
                                    render={({ field }) => (
                                        <div>
                                            <FormSelect
                                                {...field}
                                                options={areaChoices.map(([value, label]) => ({
                                                    value: value,
                                                    label: label,
                                                }))}
                                            />
                                            {errors.area && <FormError message="選択してください" />}
                                        </div>
                                    )}
                                />
                            )}
                        </FlexContainer>
                        <FlexContainer>
                            <FormLabel title="都道府県" isRequired />
                            {filteredPrefectureChoices && (
                                <Controller
                                    name="prefecture"
                                    control={control}
                                    render={({ field }) => (
                                        <div>
                                            <FormSelect
                                                {...field}
                                                options={filteredPrefectureChoices.map(([value, label]) => ({
                                                    value: value,
                                                    label: label,
                                                }))}
                                            />
                                            {errors.prefecture && <FormError message={errors.prefecture.message} />}
                                        </div>
                                    )}
                                />
                            )}
                        </FlexContainer>
                        <FlexContainer>
                            <FormLabel title="従業員規模" isRequired />
                            {employeeChoices && employeeChoices.length > 0 && (
                                <Controller
                                    name="number_of_employees"
                                    control={control}
                                    render={({ field }) => (
                                        <div>
                                            <FormSelect
                                                {...field}
                                                options={employeeChoices.map(([value, label]) => ({
                                                    value: value,
                                                    label: label,
                                                }))}
                                            />
                                            {errors.number_of_employees && (
                                                <FormError message={errors.number_of_employees.message} />
                                            )}
                                        </div>
                                    )}
                                />
                            )}
                        </FlexContainer>
                        <FlexContainer>
                            <FormLabel title="売上高" isRequired />
                            {revenueChoices && revenueChoices.length > 0 && (
                                <Controller
                                    name="revenue_scale"
                                    control={control}
                                    render={({ field }) => (
                                        <div>
                                            <FormSelect
                                                {...field}
                                                options={revenueChoices.map(([value, label]) => ({
                                                    value: value,
                                                    label: label,
                                                }))}
                                            />
                                            {errors.revenue_scale && (
                                                <FormError message={errors.revenue_scale.message} />
                                            )}
                                        </div>
                                    )}
                                />
                            )}
                        </FlexContainer>
                        <FlexContainer>
                            <FormLabel title="営業利益" isRequired />
                            {profitLevelChoices && profitLevelChoices.length > 0 && (
                                <Controller
                                    name="profit_level"
                                    control={control}
                                    render={({ field }) => (
                                        <div>
                                            <FormSelect
                                                {...field}
                                                options={profitLevelChoices.map(([value, label]) => ({
                                                    value: value,
                                                    label: label,
                                                }))}
                                            />
                                            {errors.profit_level && <FormError message={errors.profit_level.message} />}
                                        </div>
                                    )}
                                />
                            )}
                        </FlexContainer>
                        <FlexContainer>
                            <FormLabel title="純資産金額" isRequired />
                            {netAssetsLevelChoices && netAssetsLevelChoices.length > 0 && (
                                <Controller
                                    name="net_assets_level"
                                    control={control}
                                    render={({ field }) => (
                                        <div>
                                            <FormSelect
                                                {...field}
                                                options={netAssetsLevelChoices.map(([value, label]) => ({
                                                    value: value,
                                                    label: label,
                                                }))}
                                            />
                                            {errors.net_assets_level && (
                                                <FormError message={errors.net_assets_level.message} />
                                            )}
                                        </div>
                                    )}
                                />
                            )}
                        </FlexContainer>
                        <FlexContainer>
                            <FormLabel title="業種" isRequired />
                            {industryChoices && industryChoices.length > 0 && (
                                <Controller
                                    name="industry"
                                    control={control}
                                    render={({ field }) => (
                                        <div>
                                            <FormSelect
                                                {...field}
                                                options={industryChoices.map((industry) => ({
                                                    value: industry.id.toString(),
                                                    label: industry.name,
                                                }))}
                                            />
                                            {errors.industry && <FormError message={errors.industry.message} />}
                                        </div>
                                    )}
                                />
                            )}
                        </FlexContainer>
                        <FlexContainer>
                            <CompanyInfoVisibilityNotice />
                            <FormLabel title="事業内容" isRequired />
                            <Controller
                                name="business_description"
                                control={control}
                                render={({ field }) => (
                                    <div>
                                        <FormTextArea
                                            {...field}
                                            value={field.value ?? ''}
                                            hasError={!!errors.business_description?.message}
                                            resize="vertical"
                                        />
                                        {errors.business_description?.message && (
                                            <FormError message={errors.business_description.message} />
                                        )}
                                    </div>
                                )}
                            />
                        </FlexContainer>
                        <FlexContainer>
                            <FormLabel title="仲介会社に聞いてみたいこと" />
                            <Description>
                                以下の項目について、具体的に知りたい内容があればチェックして詳細を入力してください
                            </Description>

                            <CheckboxContainer>
                                <Controller
                                    name="fee_structure"
                                    control={control}
                                    render={({ field }) => (
                                        <FormCheckbox
                                            id="fee_structure"
                                            disableBorder
                                            checked={field.value}
                                            onChange={(e) => field.onChange(e.target.checked)}
                                        >
                                            M&Aに関する手数料水準等について
                                        </FormCheckbox>
                                    )}
                                />
                                {watch('fee_structure') && (
                                    <Controller
                                        name="fee_structure_txt"
                                        control={control}
                                        render={({ field }) => (
                                            <div>
                                                <FormTextArea
                                                    {...field}
                                                    value={field.value ?? ''}
                                                    hasError={!!errors.fee_structure_txt?.message}
                                                    placeholder="「M&Aに関する手数料水準等について」の詳細を入力"
                                                    resize="vertical"
                                                />
                                                {errors.fee_structure_txt?.message && (
                                                    <FormError message={errors.fee_structure_txt.message} />
                                                )}
                                            </div>
                                        )}
                                    />
                                )}

                                <Controller
                                    name="timing_considerations"
                                    control={control}
                                    render={({ field }) => (
                                        <FormCheckbox
                                            id="timing_considerations"
                                            disableBorder
                                            checked={field.value}
                                            onChange={(e) => field.onChange(e.target.checked)}
                                        >
                                            M&Aを検討し始めるタイミングについて
                                        </FormCheckbox>
                                    )}
                                />
                                {watch('timing_considerations') && (
                                    <Controller
                                        name="timing_considerations_txt"
                                        control={control}
                                        render={({ field }) => (
                                            <div>
                                                <FormTextArea
                                                    {...field}
                                                    value={field.value ?? ''}
                                                    hasError={!!errors.timing_considerations_txt?.message}
                                                    placeholder="「M&Aを検討し始めるタイミングについて」の詳細を入力"
                                                    resize="vertical"
                                                />
                                                {errors.timing_considerations_txt?.message && (
                                                    <FormError message={errors.timing_considerations_txt.message} />
                                                )}
                                            </div>
                                        )}
                                    />
                                )}

                                <Controller
                                    name="matching_potential"
                                    control={control}
                                    render={({ field }) => (
                                        <FormCheckbox
                                            id="matching_potential"
                                            disableBorder
                                            checked={field.value}
                                            onChange={(e) => field.onChange(e.target.checked)}
                                        >
                                            自社のマッチング可能性について
                                        </FormCheckbox>
                                    )}
                                />
                                {watch('matching_potential') && (
                                    <Controller
                                        name="matching_potential_txt"
                                        control={control}
                                        render={({ field }) => (
                                            <div>
                                                <FormTextArea
                                                    {...field}
                                                    value={field.value ?? ''}
                                                    hasError={!!errors.matching_potential_txt?.message}
                                                    placeholder="「自社のマッチング可能性について」の詳細を入力"
                                                    resize="vertical"
                                                />
                                                {errors.matching_potential_txt?.message && (
                                                    <FormError message={errors.matching_potential_txt.message} />
                                                )}
                                            </div>
                                        )}
                                    />
                                )}

                                <Controller
                                    name="case_studies"
                                    control={control}
                                    render={({ field }) => (
                                        <FormCheckbox
                                            id="case_studies"
                                            disableBorder
                                            checked={field.value}
                                            onChange={(e) => field.onChange(e.target.checked)}
                                        >
                                            他社事例などについて
                                        </FormCheckbox>
                                    )}
                                />
                                {watch('case_studies') && (
                                    <Controller
                                        name="case_studies_txt"
                                        control={control}
                                        render={({ field }) => (
                                            <div>
                                                <FormTextArea
                                                    {...field}
                                                    value={field.value ?? ''}
                                                    hasError={!!errors.case_studies_txt?.message}
                                                    placeholder="「他社事例などについて」の詳細を入力"
                                                    resize="vertical"
                                                />
                                                {errors.case_studies_txt?.message && (
                                                    <FormError message={errors.case_studies_txt.message} />
                                                )}
                                            </div>
                                        )}
                                    />
                                )}

                                <Controller
                                    name="process_flow"
                                    control={control}
                                    render={({ field }) => (
                                        <FormCheckbox
                                            id="process_flow"
                                            disableBorder
                                            checked={field.value}
                                            onChange={(e) => field.onChange(e.target.checked)}
                                        >
                                            M&Aの具体的な流れについて
                                        </FormCheckbox>
                                    )}
                                />
                                {watch('process_flow') && (
                                    <Controller
                                        name="process_flow_txt"
                                        control={control}
                                        render={({ field }) => (
                                            <div>
                                                <FormTextArea
                                                    {...field}
                                                    value={field.value ?? ''}
                                                    hasError={!!errors.process_flow_txt?.message}
                                                    placeholder="「M&Aの具体的な流れについて」の詳細を入力"
                                                    resize="vertical"
                                                />
                                                {errors.process_flow_txt?.message && (
                                                    <FormError message={errors.process_flow_txt.message} />
                                                )}
                                            </div>
                                        )}
                                    />
                                )}
                            </CheckboxContainer>

                            <Description>その他の仲介会社への質問や要望があれば入力してください</Description>
                            <TemplateWrapper
                                disabled={loading}
                                aria-label="テンプレートを表示"
                                onClick={handleTemplateClick}
                            >
                                テンプレートから選ぶ
                                <img src={MailIcon} alt="テンプレート" />
                            </TemplateWrapper>
                            <Controller
                                name="question"
                                control={control}
                                render={({ field }) => (
                                    <div>
                                        <FormTextArea
                                            {...field}
                                            value={field.value ?? ''}
                                            hasError={!!errors.question?.message}
                                            placeholder="その他の質問や要望があれば入力してください"
                                            resize="vertical"
                                        />
                                        {errors.question?.message && <FormError message={errors.question.message} />}
                                    </div>
                                )}
                            />
                        </FlexContainer>
                        <FlexContainer>
                            <FormLabel title="ホームページURL" />
                            <Controller
                                name="hp_url"
                                control={control}
                                render={({ field }) => (
                                    <div>
                                        <FormInput
                                            {...field}
                                            value={field.value ?? ''}
                                            hasError={!!errors.hp_url?.message}
                                        />
                                        {errors.hp_url?.message && <FormError message={errors.hp_url.message} />}
                                    </div>
                                )}
                            />
                        </FlexContainer>
                        <FlexContainer>
                            <FormLabel title={watch('is_sole_proprietor') ? 'ロゴ画像' : '企業画像'} />
                            <ImageUpload
                                imagePath={watch('icon_image') ?? ''}
                                directory="company"
                                fileName={'icon'}
                                fileCategory="company"
                                disabled={loading}
                                width={180}
                                height={180}
                                onComplete={(fileUrl) => setValue('icon_image', fileUrl)}
                            />
                        </FlexContainer>

                        <ButtonContainer>
                            <ActionButton
                                disabled={loading}
                                onClick={submit}
                                bgColor="main-color-blue"
                                style={{ minWidth: '200px', maxWidth: '400px' }}
                            >
                                設定する
                            </ActionButton>
                        </ButtonContainer>
                    </Wrapper>
                )}
                <Preview
                    company={{
                        ...watch(),
                        prefecture: getLabel(filteredPrefectureChoices, watch('prefecture')),
                        label: user?.company?.label,
                    }}
                    editing={editing}
                    setEditing={setEditing}
                    areaChoices={areaChoices}
                    prefectureChoices={filteredPrefectureChoices}
                    employeeChoices={employeeChoices}
                    revenueChoices={revenueChoices}
                    profitLevelChoices={profitLevelChoices}
                    netAssetsLevelChoices={netAssetsLevelChoices}
                    industryChoices={industryChoices}
                    unPublish={unPublish}
                    reOpen={reOpen}
                    loading={loading}
                    isPublish={!user.company?.unpublished}
                />
            </Container>
        </>
    );
};
